/**
 * MobileNavigationDrawer — slide-in navigation drawer for phones.
 *
 * Deliberately reuses the *exact same* module/group/role rules as the
 * desktop `Sidebar` (see `Sidebar.jsx`). Everything security-relevant
 * (Care Worker trim, manager-only groups, backend 403s) still applies —
 * we just render the same allowed list inside a Sheet.
 */
import React, { useMemo, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { NAV } from "@/config/modules";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import {
  LogOut, Building2, Smartphone, UsersRound, CalendarClock, HeartHandshake,
  PillBottle, Inbox, CalendarRange, ShieldAlert, Umbrella, Truck, ClipboardList,
  CalendarDays, ScrollText, ClipboardCheck, AlertOctagon, Database, TrendingUp,
  BadgePoundSterling, Pill,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

// Same allow-list as `Sidebar.jsx`.
const CARE_WORKER_ALLOWED_TESTIDS = new Set([
  "nav-dashboard", "nav-my-shifts", "nav-mar-chart",
  "nav-time-off", "nav-calendar", "nav-mobile",
  "nav-daily_notes", "nav-personal_care", "nav-food_fluid",
  "nav-vital_signs", "nav-medication", "nav-incidents",
  "nav-clients", "nav-medication-competency",
]);

const MANAGER_ROLES = ["admin", "manager", "registered_manager", "deputy_manager", "service_manager", "super_admin"];

export default function MobileNavigationDrawer({ open, onOpenChange }) {
  const location = useLocation();
  const { user, logout } = useAuth();
  const isFrontline = user?.role === "care_worker";

  // Build the same grouped nav list the desktop sidebar builds.
  const groups = useMemo(() => {
    const g = NAV.reduce((acc, item) => {
      const key = item.group || "Main";
      (acc[key] ||= []).push(item);
      return acc;
    }, {});
    const careDelivery = [
      { path: "/my-shifts", label: "My Shifts", icon: ClipboardList, testid: "nav-my-shifts" },
      { path: "/rota", label: "Rota & Visits", icon: CalendarClock, testid: "nav-rota" },
      { path: "/calendar", label: "Appointments", icon: CalendarDays, testid: "nav-calendar" },
      { path: "/care-plans", label: "Care Plans", icon: ClipboardCheck, testid: "nav-care-plans" },
      { path: "/risk-register", label: "Risk Register", icon: AlertOctagon, testid: "nav-risk-register" },
      { path: "/staff-schedule", label: "Staff Schedule", icon: CalendarRange, testid: "nav-staff-schedule" },
      { path: "/mar-chart", label: "MAR Chart", icon: PillBottle, testid: "nav-mar-chart" },
      { path: "/cd-ledger", label: "CD Ledger", icon: ShieldAlert, testid: "nav-cd-ledger" },
      { path: "/delivery-notes", label: "Delivery Notes", icon: Truck, testid: "nav-delivery-notes" },
      { path: "/time-off-requests", label: "Leave & Holidays", icon: Umbrella, testid: "nav-time-off" },
    ];
    if (MANAGER_ROLES.includes(user?.role)) {
      careDelivery.push({ path: "/family-portal", label: "Family Portal", icon: HeartHandshake, testid: "nav-family-portal" });
      careDelivery.push({ path: "/family-inbox", label: "Family Inbox", icon: Inbox, testid: "nav-family-inbox" });
    }
    g["Care Delivery"] = [...(g["Care Delivery"] || []), ...careDelivery];

    if (MANAGER_ROLES.includes(user?.role) || user?.role === "care_worker") {
      g["People"] = [
        ...(g["People"] || []),
        { path: "/medication-competency", label: "Medication Competency", icon: Pill, testid: "nav-medication-competency" },
      ];
    }

    if (MANAGER_ROLES.includes(user?.role)) {
      g["Workforce & Finance"] = [
        { path: "/payroll", label: "Payroll & Staff Hours", icon: CalendarRange, testid: "nav-payroll-staff-hours" },
        { path: "/client-care-cost", label: "Client Care Costs", icon: BadgePoundSterling, testid: "nav-client-care-cost" },
      ];
    }

    const systemItems = [];
    if (user?.role === "admin" || user?.role === "super_admin") {
      systemItems.push({ path: "/sites", label: "Sites", icon: Building2, testid: "nav-sites" });
      systemItems.push({ path: "/users", label: "Staff & Users", icon: UsersRound, testid: "nav-users" });
      systemItems.push({ path: "/migration", label: "Data Migration", icon: Database, testid: "nav-migration" });
      systemItems.push({ path: "/migration/executive", label: "Migrations · Executive", icon: TrendingUp, testid: "nav-migration-exec" });
    }
    if (!isFrontline) {
      systemItems.push({ path: "/audit", label: "Audit Centre", icon: ScrollText, testid: "nav-audit" });
    }
    systemItems.push({ path: "/mobile", label: "Mobile view", icon: Smartphone, testid: "nav-mobile" });
    if (systemItems.length) g["System"] = systemItems;

    // Care Worker trim — mirrors the desktop sidebar's role fence.
    if (isFrontline) {
      const trimmed = {};
      for (const [group, items] of Object.entries(g)) {
        const kept = items.filter((it) => CARE_WORKER_ALLOWED_TESTIDS.has(it.testid));
        if (kept.length) trimmed[group] = kept;
      }
      return trimmed;
    }
    return g;
  }, [user?.role, isFrontline]);

  // Close on route change so a nav tap dismisses the drawer.
  useEffect(() => {
    if (open) onOpenChange?.(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const renderItem = (it) => {
    const Icon = it.icon;
    const active = location.pathname === it.path
      || (it.path !== "/" && location.pathname.startsWith(it.path));
    return (
      <NavLink
        key={it.path}
        to={it.path}
        data-testid={`mobile-nav-${it.testid.replace(/^nav-/, "")}`}
        className={`flex items-center gap-3 px-3 py-3 rounded-md text-[15px] transition-colors min-h-[48px] ${
          active
            ? "bg-slate-800 text-white font-semibold shadow-[inset_4px_0_0_0_#7CC142]"
            : "text-slate-200 hover:bg-slate-800/70 active:bg-slate-800"
        }`}
      >
        {Icon && <Icon className="w-5 h-5 shrink-0" strokeWidth={1.9} />}
        <span className="truncate flex-1">{it.label}</span>
      </NavLink>
    );
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="left"
        className="w-[86vw] max-w-[320px] p-0 bg-slate-900 text-slate-100 border-r border-slate-800 flex flex-col"
        data-testid="mobile-nav-drawer"
      >
        <SheetHeader className="p-0 shrink-0">
          <Link
            to="/"
            onClick={() => onOpenChange?.(false)}
            className="flex items-center gap-3 px-5 py-5 border-b border-white/10"
            data-testid="mobile-nav-brand"
          >
            <img
              src="/brand/icon-192.png"
              alt="Ayobell"
              className="w-11 h-11 object-contain drop-shadow-[0_4px_18px_rgba(34,197,94,0.25)]"
            />
            <div className="min-w-0 text-left">
              <SheetTitle className="font-heading font-bold text-white text-lg leading-none tracking-wide">
                AYOBELL
              </SheetTitle>
              <div className="text-[10px] mt-1 font-medium text-white/70 tracking-widest">
                Care Management Platform
              </div>
            </div>
          </Link>
        </SheetHeader>

        <nav className="flex-1 min-h-0 overflow-y-auto py-3">
          {Object.entries(groups).map(([group, items]) => (
            <div key={group} className="mb-3">
              <div className="px-4 pb-1 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
                {group}
              </div>
              <ul className="space-y-0.5 px-2">
                {items.map((it) => <li key={it.path}>{renderItem(it)}</li>)}
              </ul>
            </div>
          ))}
        </nav>

        <div className="shrink-0 border-t border-slate-800 p-3 bg-slate-950">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-9 h-9 rounded-full bg-sky-600 flex items-center justify-center text-sm font-semibold shrink-0">
              {(user?.name || user?.email || "?").slice(0, 1).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate" data-testid="mobile-nav-user-name">
                {user?.name || user?.email}
              </div>
              <div className="text-[10px] text-slate-400 uppercase tracking-widest">
                {(user?.role || "").replace(/_/g, " ")}
              </div>
            </div>
          </div>
          <button
            data-testid="mobile-nav-logout"
            onClick={() => { onOpenChange?.(false); logout(); }}
            className="w-full flex items-center justify-center gap-2 text-sm h-11 rounded-md bg-slate-800 hover:bg-slate-700 active:bg-slate-700 text-slate-100"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
